import { Typography } from '@/ui-kit/';
import styles from './target-info-registration-form.module.scss';
import layoutStyles from '../../form-registration.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '../button-submit-step';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { CheckIcon, SuccessNotificationIcon } from '@/assets/icons';
import {
    registrationStepSixSchema,
    RegistrationStepSixType,
} from '@/schemes/registrationV2/registration-step-six-schema';
import clsx from 'clsx';
import { ZodEnum, ZodOptional } from 'zod';
import { eventsApi } from '@/api';
import { ChatIcon } from '@/assets/icons';
import { HeartIcon } from '@/assets/icons';
import { PurposeNotSpecifiedIcon } from '@/assets/icons';
import { useTranslation } from 'react-i18next';
import { SignUpActions } from '@/components/sign-up-actions/sign-up-actions';
const DELAY_SEND_DATA = 1000;

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepSixType) => Promise<any>;
    nextStep: () => void;
}

export const TargetInfoRegistrationForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    useEffect(() => {
        callYaMetrika('new_purpose_screen');
        eventsApi.updateUserEvent('new_purpose_screen');
        eventsApi.updateUserEvent('registration_step_view_purpose_screen')
    }, []);

    const initialValues = useMemo(() => {
        return {
            purpose: userData?.purpose ?? 'not_specified',
        };
    }, [userData]);

    const [isPurposeSelected, setIsPurposeSelected] = useState<Profile['purpose']>(initialValues.purpose);
    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepSixType>({
        resolver: zodResolver(registrationStepSixSchema),
        defaultValues: initialValues,
    });

    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
    } = methods;

    const onSubmit = async (data: RegistrationStepSixType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             *     await onUpdateUserData({ state: 'complete', ...data });
             * }
             */
            await onUpdateUserData(data);

            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_purpose_screen')
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const handleChangePurpose = (value: any) => {
        methods.setValue('purpose', value);
        setIsPurposeSelected(value);
        handleChangeUserDataDebounce({ purpose: value });
    };

    const { t } = useTranslation();

    return (
        <FormProvider {...methods}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('what_are_you_here_for')}
                    </Typography>

                    <p className={styles.formInfoText}>{t('registration.target_note')}</p>

                    <div className={styles.formContainerTarget}>
                        <div
                            className={clsx(
                                styles.formContainerTargetItem,
                                isPurposeSelected === 'fun' && styles.formContainerTargetItemActive,
                            )}
                            onClick={() => handleChangePurpose('fun')}>
                            <div className={styles.formContainerTargetItemPhoto}>
                                <ChatIcon className={styles.newFriendsIcon} />
                            </div>
                            <div className={styles.formContainerTargetItemText}>
                                <h3>{t('fun')}</h3>
                                <p>{t('fun_desc')}</p>
                            </div>

                            {isPurposeSelected === 'fun' && (
                                <CheckIcon
                                    width={20}
                                    height={20}
                                    className={styles.iconTop} />
                            )}
                        </div>

                        <div
                            onClick={() => handleChangePurpose('love')}
                            className={clsx(
                                styles.formContainerTargetItem,
                                isPurposeSelected === 'love' && styles.formContainerTargetItemActive,
                            )}>
                            <div className={styles.formContainerTargetItemPhoto}>
                                <HeartIcon className={styles.romanticIcon} />
                            </div>
                            <div className={styles.formContainerTargetItemText}>
                                <h3>{t('love')}</h3>
                                <p>{t('love_desc')}</p>
                            </div>

                            {isPurposeSelected === 'love' && (
                                <CheckIcon
                                    width={20}
                                    height={20}
                                    className={styles.iconTop} />
                            )}
                        </div>

                        <div
                            onClick={() => handleChangePurpose('not_specified')}
                            className={clsx(
                                styles.formContainerTargetItem,
                                isPurposeSelected === 'not_specified' && styles.formContainerTargetItemActive,
                            )}>
                            <div className={styles.formContainerTargetItemPhoto}>
                                <PurposeNotSpecifiedIcon className={styles.howGoesIcon} />
                            </div>
                            <div className={styles.formContainerTargetItemText}>
                                <h3>{t('not_specified')}</h3>
                                <p>{t('not_specified_desc')}</p>
                            </div>

                            {isPurposeSelected === 'not_specified' && (
                                <CheckIcon
                                    width={20}
                                    height={20}
                                    className={styles.iconTop} />
                            )}
                        </div>
                    </div>
                </div>

                <SignUpActions className={layoutStyles.actions}>
                    <ButtonSubmitStep
                        isLoading={isLoading}
                        stepNumber={1}
                        isDisabled={!isValid} />
                </SignUpActions>
            </form>
        </FormProvider>
    );
};
